import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * PortalOverlay — shared wrapper for every bottom-sheet / centered modal in the app.
 *
 * Why this exists: the PWA shell uses `overflow: hidden + height: 100dvh` with a
 * fixed-position bottom navigation at z-40. A naive `position: fixed` modal rendered
 * inside `.main-content` gets clipped below the nav (we hit this 3 separate times —
 * PdfViewerModal, Tooltip, JobCompletionDialog). Always mounting via React portal to
 * `document.body` solves it once and for all.
 *
 * Usage:
 *   <PortalOverlay open={open} onClose={close} placement="bottom">
 *     <div className="bg-card p-4">...</div>
 *   </PortalOverlay>
 *
 * The child is the modal *card* itself; this wrapper handles:
 *  • React portal to document.body
 *  • Backdrop (bg-black/40) + click-outside-to-close
 *  • Escape-to-close
 *  • Body-scroll lock while open
 *  • iOS safe-area bottom padding, and 100dvh so Safari's bottom bar can
 *    never cover the action row (iter370)
 *  • z-index above bottom nav (z-[100])
 */
export default function PortalOverlay({
  open,
  onClose,
  placement = 'center', // 'center' | 'bottom'
  backdropClassName = 'bg-black/40',
  wrapperClassName = '',
  children,
  testId = 'portal-overlay',
}) {
  // Body-scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const alignClass = placement === 'bottom'
    ? 'items-end sm:items-center'
    : 'items-center';

  return createPortal(
    <div
      className={`modal-overlay z-[100] flex justify-center ${alignClass} ${backdropClassName} ${wrapperClassName}`}
      onClick={onClose}
      data-testid={testId}
    >
      {/* iter370: Safari's bottom bar covered the action row of centred
          modals too, not only bottom sheets, so both get the safe-area inset
          and the card can never be taller than the dynamic viewport. */}
      <div
        onClick={e => e.stopPropagation()}
        className="w-full sm:w-auto pointer-events-auto flex flex-col min-h-0 max-h-full"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
